import React from 'react';
import { Switch, Select } from 'antd';

const { Option } = Select;
import {
  EditFormData,
  UrlRule,
  ResponseRule,
  NestedRuleGroup,
  NestedResponseGroup,
} from '../types';
import NestedRuleGroupComponent from './NestedRuleGroup';
import NestedResponseGroupComponent from './NestedResponseGroup';
import LivePreview from './LivePreview';
import { LogicRadioGroup } from './StyledRadioButtons';

interface ConfigurationRuleEditProps {
  formData: EditFormData;
  onFormDataChange: (data: EditFormData) => void;
  onAddUrlRule: (groupId?: string) => void;
  onUpdateUrlRule: (groupId: string, ruleId: string, field: keyof UrlRule, value: any) => void;
  onRemoveUrlRule: (groupId: string, ruleId: string) => void;
  onAddUrlGroup: (parentGroupId?: string) => void;
  onUpdateUrlGroup: (groupId: string, updates: Partial<NestedRuleGroup>) => void;
  onRemoveUrlGroup: (groupId: string) => void;
  onAddResponseRule: (groupId?: string) => void;
  onUpdateResponseRule: (
    groupId: string,
    ruleId: string,
    field: keyof ResponseRule,
    value: any,
  ) => void;
  onRemoveResponseRule: (groupId: string, ruleId: string) => void;
  onAddResponseGroup: (parentGroupId?: string) => void;
  onUpdateResponseGroup: (groupId: string, updates: Partial<NestedResponseGroup>) => void;
  onRemoveResponseGroup: (groupId: string) => void;
  isNewConfiguration?: boolean; // Add prop to determine if it's new or edit
}

const ConfigurationRuleEdit: React.FC<ConfigurationRuleEditProps> = ({
  formData,
  onFormDataChange,
  onAddUrlRule,
  onUpdateUrlRule,
  onRemoveUrlRule,
  onAddUrlGroup,
  onUpdateUrlGroup,
  onRemoveUrlGroup,
  onAddResponseRule,
  onUpdateResponseRule,
  onRemoveResponseRule,
  onAddResponseGroup,
  onUpdateResponseGroup,
  onRemoveResponseGroup,
  isNewConfiguration = false,
}) => {
  // Create a root group for URL rules to handle nested structure
  const urlRootGroup: NestedRuleGroup = {
    id: 'url-root',
    type: formData.urlRuleOperator,
    rules: formData.urlRules,
  };

  // Create a root group for Response rules to handle nested structure
  const responseRootGroup: NestedResponseGroup = {
    id: 'response-root',
    type: formData.responseRuleOperator,
    rules: formData.responseRules,
  };
  return (
    <div
      style={{
        backgroundColor: 'transparent',
        padding: '0px 12px',
      }}
    >
      {/* No header buttons - moved to floating section */}
      <div style={{ marginBottom: 24 }}></div>

      {/* Main content with form on left and preview on right */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        {/* Form section - left side */}
        <div style={{ flex: 1, marginBottom: 24 }}>
          {/* Section 1: Collect/Block */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ marginBottom: 16 }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'rgba(76, 76, 76, 1)',
                }}
              >
                1. Collect/Block <span style={{ color: '#ff4d4f' }}>*</span>
              </h3>
              <p
                style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#8c8c8c', lineHeight: 1.4 }}
              >
                Choose whether to collect or block network requests that match the specified
                conditions.
              </p>
            </div>

            <Select
              value={formData.allowBlock}
              onChange={(value) => onFormDataChange({ ...formData, allowBlock: value })}
              style={{ width: 200 }}
              placeholder="Choose action"
              data-test-id="collect-block-select"
            >
              <Option value="collect">Collect</Option>
              <Option value="block">Block</Option>
            </Select>
          </div>

          {/* Section 2: URL */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ marginBottom: 16 }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'rgba(76, 76, 76, 1)',
                }}
              >
                2. URL <span style={{ color: '#ff4d4f' }}>*</span>
              </h3>
              <p
                style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#8c8c8c', lineHeight: 1.4 }}
              >
                Define URL patterns to match network requests based on host, path, or scheme
                components.
              </p>
            </div>

            {/* For All URL Toggle */}
            <div
              style={{
                marginBottom: 30,
                marginTop: 20,
                display: 'flex',
                alignItems: 'center',

                maxWidth: '300px',
              }}
            >
              <span style={{ fontSize: '14px', fontWeight: '500', color: 'rgba(76, 76, 76, 1)' }}>
                Applicable for all URLs
              </span>
              <Switch
                style={{
                  marginLeft: 16,
                }}
                size="small"
                checked={formData.forAllUrl || false}
                onChange={(checked) => onFormDataChange({ ...formData, forAllUrl: checked })}
                data-test-id="for-all-url-toggle"
              />
            </div>

            <div
              style={{
                marginBottom: 16,
                opacity: formData.forAllUrl ? 0.5 : 1,
                pointerEvents: formData.forAllUrl ? 'none' : 'auto',
              }}
            >
              <span style={{ marginRight: 8, fontSize: '14px', fontWeight: '500' }}>Logic:</span>
              <LogicRadioGroup
                value={formData.urlRuleOperator}
                onChange={(value) => onFormDataChange({ ...formData, urlRuleOperator: value })}
                disabled={formData.forAllUrl}
                testId="url-logic-radio-group"
              />
            </div>

            <div
              style={{
                opacity: formData.forAllUrl ? 0.5 : 1,
                pointerEvents: formData.forAllUrl ? 'none' : 'auto',
              }}
            >
              <NestedRuleGroupComponent
                group={urlRootGroup}
                onUpdate={(groupId, updates) => {
                  if (groupId === 'url-root') {
                    onFormDataChange({
                      ...formData,
                      urlRuleOperator: updates.type || formData.urlRuleOperator,
                      urlRules: updates.rules || formData.urlRules,
                    });
                  } else {
                    onUpdateUrlGroup(groupId, updates);
                  }
                }}
                onRemove={onRemoveUrlGroup}
                onAddCondition={(groupId) => {
                  onAddUrlRule(groupId);
                }}
                onAddGroup={onAddUrlGroup}
                onUpdateRule={onUpdateUrlRule}
                onRemoveRule={onRemoveUrlRule}
                level={0}
              />
            </div>
          </div>

          {/* AND Connector between URL and Response sections */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 24,
              marginTop: -8,
            }}
          >
            <div
              style={{
                flex: 1,
                height: '1px',
                backgroundColor: '#d9d9d9',
              }}
            ></div>
            <span
              style={{
                margin: '0 16px',
                fontSize: '14px',
                fontWeight: '600',
                color: 'rgba(0, 0, 0, 0.65)',
                backgroundColor: 'white',
                padding: '0 8px',
              }}
            >
              AND
            </span>
            <div
              style={{
                flex: 1,
                height: '1px',
                backgroundColor: '#d9d9d9',
              }}
            ></div>
          </div>

          {/* Section 3: Response Code */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'rgba(76, 76, 76, 1)',
                }}
              >
                3. Response Code and Duration
                {formData.forAllUrl && <span style={{ color: '#ff4d4f' }}> *</span>}
              </h3>
              <p
                style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#8c8c8c', lineHeight: 1.4 }}
              >
                {formData.forAllUrl
                  ? 'Add conditions based on response attributes like duration or status code.'
                  : 'Optional: Add conditions based on response attributes like duration or status code.'}
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <span style={{ marginRight: 8, fontSize: '14px', fontWeight: '500' }}>Logic:</span>
              <LogicRadioGroup
                value={formData.responseRuleOperator}
                onChange={(value) => onFormDataChange({ ...formData, responseRuleOperator: value })}
                testId="response-logic-radio-group"
              />
            </div>

            <NestedResponseGroupComponent
              group={responseRootGroup}
              onUpdate={(groupId, updates) => {
                if (groupId === 'response-root') {
                  onFormDataChange({
                    ...formData,
                    responseRuleOperator: updates.type || formData.responseRuleOperator,
                    responseRules: updates.rules || formData.responseRules,
                  });
                } else {
                  onUpdateResponseGroup(groupId, updates);
                }
              }}
              onRemove={onRemoveResponseGroup}
              onAddCondition={(groupId) => {
                onAddResponseRule(groupId);
              }}
              onAddGroup={onAddResponseGroup}
              onUpdateRule={onUpdateResponseRule}
              onRemoveRule={onRemoveResponseRule}
              level={0}
            />
          </div>
        </div>

        {/* Live Preview section - right side */}
        <LivePreview formData={formData} />
      </div>
    </div>
  );
};

export default ConfigurationRuleEdit;
